import React, { useState, useCallback } from "react";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-hot-toast";
import userService from "../../api/services/userService";
import uploadService from "../../api/services/uploadService";

const inputClass =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";
const labelClass = "block text-xs font-semibold text-gray-600 mb-1";

const initialForm = {
  email: "",
  firstName: "",
  lastName: "",
  contactNo: "",
  gender: "male",
  birthDate: "",
};

export default function CreateAppUserModal({ isOpen, onClose, onCreated }) {
  const [form, setForm] = useState(initialForm);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = useCallback(() => {
    setForm(initialForm);
    setFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setResult(null);
  }, [previewUrl]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFile(f || null);
    if (f && f.type.startsWith("image/")) {
      setPreviewUrl(URL.createObjectURL(f));
    }
  };

  const copyPassword = () => {
    const pw = result?.temporaryPassword;
    if (!pw || !navigator.clipboard) return;
    navigator.clipboard.writeText(pw).then(
      () => toast.success("Password copied"),
      () => toast.error("Could not copy")
    );
  };

  const handleDone = () => {
    onCreated?.();
    handleClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const email = form.email.trim();
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const contactNo = form.contactNo.trim();
    const birthDate = form.birthDate;

    if (!email || !firstName || !lastName || !contactNo || !birthDate) {
      toast.error("Fill email, first name, last name, phone, and birth date.");
      return;
    }

    setSubmitting(true);
    try {
      let imageUrl = null;
      if (file) {
        const up = await uploadService.single(file);
        imageUrl = up?.imageUrl || null;
        if (!imageUrl) {
          toast.error(up?.message || "Image upload failed");
          setSubmitting(false);
          return;
        }
      }

      const res = await userService.createAppUser({
        email,
        firstName,
        lastName,
        contactNo,
        gender: form.gender,
        birthDate,
        ...(imageUrl ? { imageUrl } : {}),
      });

      if (res?.responseCode === 201 || res?.success) {
        toast.success(res?.message || "User created");
        setResult(res);
      } else {
        toast.error(res?.message || "Create failed");
      }
    } catch (err) {
      const msg =
        (err && typeof err === "object" && err.message) ||
        (typeof err === "string" ? err : null) ||
        "Failed to create user";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-user-title">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
          <h2 id="create-user-title" className="text-lg font-bold text-gray-900">
            Create app user
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-200 hover:text-gray-800"
            aria-label="Close">
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {result ? (
          <div className="px-5 py-5 space-y-4 overflow-y-auto">
            <p className="text-sm text-gray-700">
              User <strong>#{result.user?.id}</strong> was created. Share the temporary password below with them (it is
              not shown again).
            </p>
            <div>
              <span className={labelClass}>Temporary password</span>
              <div className="flex gap-2 items-stretch">
                <code className="flex-1 rounded-lg bg-gray-100 border border-gray-200 px-3 py-2 text-xs break-all">
                  {result.temporaryPassword}
                </code>
                <button
                  type="button"
                  onClick={copyPassword}
                  className="shrink-0 px-3 py-2 rounded-lg text-sm font-semibold bg-gray-200 hover:bg-gray-300 text-gray-800">
                  Copy
                </button>
              </div>
            </div>
            {result.user?.imageUrl ? (
              <div>
                <span className={labelClass}>Profile image</span>
                <img
                  src={result.user.imageUrl}
                  alt=""
                  className="h-20 w-20 rounded-full object-cover border border-gray-200"
                />
              </div>
            ) : null}
            <button
              type="button"
              onClick={handleDone}
              className="w-full py-2.5 rounded-lg text-sm font-semibold text-white bg-red-500 hover:bg-red-600">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
            <p className="text-xs text-gray-500">
              Creates a customer account with a random password (shown after success). No email or SMS verification.
              Optional: upload a profile photo (sent to your CDN via the existing admin upload endpoint).
            </p>

            <div>
              <label className={labelClass}>Profile photo (optional)</label>
              <input type="file" accept="image/*" onChange={handleFile} className="text-sm text-gray-700 w-full" />
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt=""
                  className="mt-2 h-24 w-24 rounded-full object-cover border-2 border-gray-200"
                />
              ) : null}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={labelClass}>Email</label>
                <input
                  name="email"
                  type="email"
                  autoComplete="off"
                  value={form.email}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>First name</label>
                <input name="firstName" value={form.firstName} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Last name</label>
                <input name="lastName" value={form.lastName} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Gender</label>
                <select name="gender" value={form.gender} onChange={handleChange} className={inputClass}>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Contact (phone)</label>
                <input name="contactNo" value={form.contactNo} onChange={handleChange} className={inputClass} required />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Birth date</label>
                <input name="birthDate" type="date" value={form.birthDate} onChange={handleChange} className={inputClass} required />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2 pb-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200">
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-50">
                {submitting ? "Creating…" : "Create user"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
